import { useState, useEffect } from 'react';

/**
 * Release version tracking singleton
 * Stores the API release version from response headers
 */
let currentVersion = null;
const listeners = new Set();

export function setVersionFromHeader(version) {
  if (version && version !== currentVersion) {
    currentVersion = version;
    listeners.forEach(listener => listener(version));
  }
}

export function getCurrentVersion() {
  return currentVersion;
}

/**
 * Compare two semver versions
 * @param {string} v1 - First version (e.g., '1.2.0')
 * @param {string} v2 - Second version (e.g., '1.1.0')
 * @returns {number} -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
function compareVersions(v1, v2) {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * useRelease Hook
 *
 * Tracks the API release version from X-Release response header.
 * Provides version comparison utilities for feature flags.
 *
 * @returns {Object} { version, isLoading, hasMinVersion, isVersion }
 *
 * @example
 * function NewFeature() {
 *   const { hasMinVersion } = useRelease();
 *   if (!hasMinVersion('2.0.0')) return null;
 *   return <div>New feature available in v2.0.0+</div>;
 * }
 */
export function useRelease() {
  const [version, setVersion] = useState(currentVersion);
  const [isLoading, setIsLoading] = useState(currentVersion === null);

  useEffect(() => {
    const listener = (newVersion) => {
      setVersion(newVersion);
      setIsLoading(false);
    };

    listeners.add(listener);

    if (currentVersion !== null) {
      setVersion(currentVersion);
      setIsLoading(false);
    }

    return () => {
      listeners.delete(listener);
    };
  }, []);

  /**
   * Check if current version is >= minVersion
   * @param {string} minVersion - Minimum required version
   * @returns {boolean}
   */
  const hasMinVersion = (minVersion) => {
    if (!version) return false;
    return compareVersions(version, minVersion) >= 0;
  };

  /**
   * Check if current version exactly matches
   * @param {string} targetVersion - Version to match
   * @returns {boolean}
   */
  const isVersion = (targetVersion) => {
    return version === targetVersion;
  };

  return {
    version,
    isLoading,
    hasMinVersion,
    isVersion,
  };
}

/**
 * FeatureFlag Component
 *
 * Conditionally renders children based on API version.
 *
 * @example
 * <FeatureFlag minVersion="2.0.0">
 *   <NewButton />
 * </FeatureFlag>
 *
 * @example
 * <FeatureFlag minVersion="2.0.0" fallback={<OldButton />}>
 *   <NewButton />
 * </FeatureFlag>
 */
export function FeatureFlag({ minVersion, children, fallback = null }) {
  const { hasMinVersion, isLoading } = useRelease();

  if (isLoading) {
    return fallback;
  }

  return hasMinVersion(minVersion) ? children : fallback;
}

export default useRelease;
